import { useEffect, useRef, useState } from "react";
import { Archive, ArrowCounterClockwise, Trash, CaretDown, CaretRight, CircleNotch, Warning } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { api, logUndoEvent } from "@/lib/api";
import { toast } from "sonner";
import UndoCountdownChip from "@/components/UndoCountdownChip";

const CONFIRM_THRESHOLD_KEY = "stratos.restore.confirmThreshold";
const CONFIRM_THRESHOLDS = [2, 3, 5];

// Iter 47 — read the cached threshold so first paint doesn't flash the
// default 2 before the backend responds. localStorage still acts as the
// offline/unauth fallback if the ``/settings/notifications`` fetch fails.
function readCachedThreshold() {
  try {
    const raw = localStorage.getItem(CONFIRM_THRESHOLD_KEY);
    const parsed = raw ? parseInt(raw, 10) : 2;
    return CONFIRM_THRESHOLDS.includes(parsed) ? parsed : 2;
  } catch { return 2; }
}

/**
 * Collapsible "View archived" panel for the Bots page. Hidden entirely when
 * the caller has no archived bots. Each row exposes a Restore action
 * (un-archives but keeps the bot disabled, forcing an explicit re-enable)
 * and a permanent Delete action.
 *
 * Fetches ``/bots?archived=true`` on mount and after every mutation so the
 * count stays fresh without polling.
 */
export default function ArchivedBotsCard({ onChange }) {
  const [bots, setBots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null); // { id, kind } — restore | delete
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [enableOnRestore, setEnableOnRestore] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmThreshold, setConfirmThreshold] = useState(readCachedThreshold);
  // Track whether the initial server value has been hydrated so we don't
  // accidentally overwrite the persisted setting with the localStorage
  // default before the fetch resolves.
  const hydratedRef = useRef(false);

  // Iter 47 — Backend is source of truth. Fetch on mount, mirror to
  // localStorage so subsequent tabs/loads hydrate instantly. If the request
  // fails (e.g. offline), we silently fall back to whatever the local
  // cache held.
  useEffect(() => {
    let cancelled = false;
    api.get("/settings/notifications")
      .then((r) => {
        if (cancelled) return;
        const raw = r.data?.restore_confirm_threshold;
        const val = CONFIRM_THRESHOLDS.includes(raw) ? raw : 2;
        setConfirmThreshold(val);
        try { localStorage.setItem(CONFIRM_THRESHOLD_KEY, String(val)); } catch {}
      })
      .catch(() => {})
      .finally(() => { hydratedRef.current = true; });
    return () => { cancelled = true; };
  }, []);

  const load = () => {
    setLoading(true);
    api.get("/bots?archived=true")
      .then((r) => setBots(r.data.bots || []))
      .catch(() => setBots([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const restore = async (bot) => {
    setBusy({ id: bot.id, kind: "restore" });
    try {
      await api.post(`/bots/${bot.id}/restore`);
      toast.success(`${bot.symbol} restored — still disabled, re-enable when you're ready`);
      load();
      onChange?.();
    } catch {
      toast.error(`Couldn't restore ${bot.symbol}`);
    } finally {
      setBusy(null);
    }
  };

  const del = async (bot) => {
    if (!window.confirm(`Delete ${bot.symbol} ${bot.strategy} bot permanently? Trade history in your positions is preserved.`)) return;
    setBusy({ id: bot.id, kind: "delete" });
    try {
      await api.delete(`/bots/${bot.id}`);
      toast.success(`${bot.symbol} deleted`);
      load();
      onChange?.();
    } catch {
      toast.error(`Couldn't delete ${bot.symbol}`);
    } finally {
      setBusy(null);
    }
  };

  const toggleSelected = (id) => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected((s) => (s.size === bots.length ? new Set() : new Set(bots.map((b) => b.id))));
  };

  // Bulk restore — parallel POSTs since the backend is idempotent and each
  // call is independent. Summary toast at the end. Undoes a mis-clicked
  // bulk archive in a single tap. When ``enableOnRestore`` is true, the
  // backend also flips ``enabled:true`` for each restored bot.
  const performRestore = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    setBulkBusy(true);
    const toastId = "archived-bulk-restore";
    const enableSuffix = enableOnRestore ? " and enable" : "";
    toast.loading(`Restoring${enableSuffix} ${ids.length} bots…`, { id: toastId });
    const results = await Promise.allSettled(
      ids.map((id) => api.post(`/bots/${id}/restore${enableOnRestore ? "?enable=true" : ""}`)),
    );
    const ok = results.filter((r) => r.status === "fulfilled").length;
    const failed = results.length - ok;
    const msg = enableOnRestore ? "Restored & enabled" : "Restored";
    if (failed === 0) toast.success(`${msg} ${ok} bot${ok === 1 ? "" : "s"}`, { id: toastId });
    else toast.info(`${msg} ${ok} · ${failed} failed`, { id: toastId });
    setSelected(new Set());
    setBulkBusy(false);
    load();
    onChange?.();
  };

  // Guard: when the user is about to reactivate more than one bot at once,
  // ask for confirmation first. Threshold is user-configurable via the
  // dropdown in the bulk bar (persisted in localStorage). Single-select
  // paths and non-enable paths always skip the prompt.
  const restoreSelected = () => {
    if (enableOnRestore && selected.size >= confirmThreshold) {
      setConfirmOpen(true);
      return;
    }
    performRestore();
  };

  const changeThreshold = (n) => {
    const previous = confirmThreshold;
    setConfirmThreshold(n);
    // Local cache stays in sync so the next page-load hydrates instantly.
    try { localStorage.setItem(CONFIRM_THRESHOLD_KEY, String(n)); } catch {}
    // Persist to backend so the preference follows the user across
    // browsers. Failures are non-fatal — the local cache still holds.
    api.put("/settings/notifications", { restore_confirm_threshold: n }).catch(() => {});
    // Iter 50 — Undo pattern extended here. If the user picks 3 or 5 when
    // they meant 2 (fat-finger the dropdown), one tap on the toast rolls
    // both the UI and the backend back to `previous`. Skip the toast when
    // the value didn't actually change (identity-select).
    if (n === previous) return;
    toast.success(`Confirm threshold set to ${n}`, {
      duration: 8000,
      description: <UndoCountdownChip duration={8000} kind="threshold" />,
      action: {
        label: "Undo",
        onClick: () => {
          logUndoEvent("clicked", "threshold");
          setConfirmThreshold(previous);
          try { localStorage.setItem(CONFIRM_THRESHOLD_KEY, String(previous)); } catch {}
          api.put("/settings/notifications", { restore_confirm_threshold: previous }).catch(() => {});
          toast.success("Reverted");
        },
      },
    });
    logUndoEvent("shown", "threshold");
  };

  if (loading) return null;
  if (bots.length === 0) return null;

  return (
    <div
      data-testid="archived-bots-card"
      className="border border-zinc-800 rounded-sm bg-zinc-900/40"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="archived-bots-toggle"
        className="w-full px-4 py-2.5 flex items-center justify-between hover:bg-zinc-900/70 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Archive size={13} className="text-zinc-500" />
          <span className="mono text-[10px] uppercase tracking-widest text-zinc-400">
            View archived bots · {bots.length}
          </span>
        </div>
        {open
          ? <CaretDown size={12} className="text-zinc-500" />
          : <CaretRight size={12} className="text-zinc-500" />}
      </button>
      {open && (
        <>
          {/* Bulk action bar — visible when the list is expanded. Compact
              on empty selection, foregrounds the count + Restore selected
              CTA when 1+ rows are checked. */}
          <div
            className="flex items-center justify-between gap-2 px-4 py-2 border-t border-zinc-800 bg-black/40"
            data-testid="archived-bulk-bar"
          >
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                data-testid="archived-select-all"
                checked={selected.size === bots.length && bots.length > 0}
                ref={(el) => {
                  if (el) el.indeterminate = selected.size > 0 && selected.size < bots.length;
                }}
                onChange={toggleAll}
                className="w-3 h-3 accent-[#00C805]"
              />
              <span className="mono text-[9px] uppercase tracking-widest text-zinc-500">
                {selected.size === 0 ? `Select bots to restore in bulk` : `${selected.size} selected`}
              </span>
            </label>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-1.5 cursor-pointer select-none">
                <input
                  type="checkbox"
                  data-testid="restore-enable-toggle"
                  checked={enableOnRestore}
                  onChange={(e) => setEnableOnRestore(e.target.checked)}
                  className="w-3 h-3 accent-[#00C805]"
                />
                <span className="mono text-[9px] uppercase tracking-widest text-zinc-500">
                  Also enable
                </span>
              </label>
              {enableOnRestore && (
                <label className="flex items-center gap-1.5 select-none" title="Show the confirm dialog when reactivating this many bots at once">
                  <span className="mono text-[9px] uppercase tracking-widest text-zinc-600">
                    Confirm ≥
                  </span>
                  <select
                    data-testid="restore-confirm-threshold"
                    value={confirmThreshold}
                    onChange={(e) => changeThreshold(parseInt(e.target.value, 10))}
                    className="mono text-[9px] uppercase tracking-widest bg-zinc-950 border border-zinc-800 rounded-sm px-1.5 py-0.5 text-white focus:outline-none focus:border-zinc-600"
                  >
                    {CONFIRM_THRESHOLDS.map((n) => (
                      <option key={n} value={n} data-testid={`restore-threshold-opt-${n}`}>{n}</option>
                    ))}
                  </select>
                </label>
              )}
              <button
                type="button"
                data-testid="bulk-restore-btn"
                onClick={restoreSelected}
                disabled={selected.size === 0 || bulkBusy}
                className="mono text-[9px] uppercase tracking-widest px-2.5 py-1 rounded-sm border border-[#00C805]/50 text-[#00C805] hover:bg-[#00C805]/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors inline-flex items-center gap-1"
              >
                {bulkBusy
                  ? <><CircleNotch size={9} className="animate-spin" /> Restoring…</>
                  : <><ArrowCounterClockwise size={9} weight="bold" />
                      {enableOnRestore ? "Restore & enable" : "Restore selected"}
                    </>
                }
              </button>
            </div>
          </div>
          <ul className="divide-y divide-zinc-900" data-testid="archived-bots-list">
            {bots.map((bot) => (
              <li
                key={bot.id}
                data-testid={`archived-bot-row-${bot.symbol}-${bot.id.slice(-6)}`}
                className="flex items-center gap-3 px-4 py-2.5 hover:bg-zinc-900/40"
              >
                <input
                  type="checkbox"
                  data-testid={`archived-select-${bot.id}`}
                  checked={selected.has(bot.id)}
                  onChange={() => toggleSelected(bot.id)}
                  className="w-3 h-3 accent-[#00C805]"
                />
                <div className="min-w-0 flex-1">
                  <div className="mono text-sm font-bold text-white">
                    {bot.symbol}{" "}
                    <span className="text-[10px] text-zinc-500 uppercase tracking-widest font-normal">
                      {bot.strategy}
                    </span>
                  </div>
                  <div className="mono text-[10px] text-zinc-500 mt-0.5">
                    Archived {bot.archived_at
                      ? new Date(bot.archived_at).toLocaleString(undefined, {
                          month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
                        })
                      : "recently"}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={() => restore(bot)}
                    disabled={busy?.id === bot.id || bulkBusy}
                    data-testid={`restore-bot-${bot.id}`}
                    className="mono text-[9px] uppercase tracking-widest px-2 py-1 rounded-sm border border-[#00C805]/50 text-[#00C805] hover:bg-[#00C805]/10 disabled:opacity-50 disabled:cursor-wait transition-colors inline-flex items-center gap-1"
                  >
                    {busy?.id === bot.id && busy?.kind === "restore"
                      ? <><CircleNotch size={9} className="animate-spin" /> Restoring…</>
                      : <><ArrowCounterClockwise size={9} weight="bold" /> Restore</>
                    }
                  </button>
                  <button
                    type="button"
                    onClick={() => del(bot)}
                    disabled={busy?.id === bot.id || bulkBusy}
                    data-testid={`delete-archived-bot-${bot.id}`}
                    title="Permanently delete this bot"
                    className="mono text-[9px] uppercase tracking-widest px-2 py-1 rounded-sm border border-zinc-700 text-zinc-500 hover:border-red-500/60 hover:text-red-400 disabled:opacity-50 disabled:cursor-wait transition-colors inline-flex items-center gap-1"
                  >
                    {busy?.id === bot.id && busy?.kind === "delete"
                      ? <><CircleNotch size={9} className="animate-spin" /> Deleting…</>
                      : <><Trash size={9} weight="bold" /> Delete</>
                    }
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
      {/* Confirm dialog — mounted here so it lives inside the card doc but
          the underlying Dialog portals to <body> so overlay z-index is
          predictable regardless of parent stacking context. */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent
          data-testid="restore-enable-confirm"
          className="bg-zinc-900 border-zinc-800 rounded-sm max-w-sm"
        >
          <DialogHeader>
            <DialogTitle className="heading text-xl tracking-tighter flex items-center gap-2">
              <Warning size={18} className="text-amber-400" weight="fill" />
              Reactivate {selected.size} bots?
            </DialogTitle>
            <DialogDescription className="text-zinc-400 text-xs mt-2">
              Restoring and enabling {selected.size} bots at once will start them trading
              on their next scheduler tick (roughly every 2 minutes). Make sure
              you still trust each strategy — chronic under-performers should
              stay disabled or be permanently deleted.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-4 flex flex-row justify-end gap-2">
            <button
              type="button"
              onClick={() => setConfirmOpen(false)}
              data-testid="restore-enable-cancel"
              className="mono text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-sm border border-zinc-700 text-zinc-400 hover:bg-zinc-800"
            >
              Cancel
            </button>
            <button
              type="button"
              data-testid="restore-enable-confirm-btn"
              onClick={() => { setConfirmOpen(false); performRestore(); }}
              className="mono text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-sm bg-[#00C805] text-black hover:bg-[#00C805]/80 inline-flex items-center gap-1"
            >
              <ArrowCounterClockwise size={10} weight="bold" />
              Restore & enable {selected.size}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
